import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { allAnswered, toggleAnswer } from '../../../../shared/courses'
import Confetti from '../../../../UI/Confetti'

import Completion from './Completion'
import PreviewBanner from './PreviewBanner'
import { useCourse } from './data'

import styles from './style.module.scss'

/**
 * Das Quiz am Ende eines Moduls.
 *
 * Die Fragen zieht der Server (zufällig aus dem Pool, Antworten gemischt) und
 * schickt sie **ohne** die richtigen Antworten. Bewertet wird erst beim Abgeben,
 * ebenfalls auf dem Server — die Oberfläche kann nicht schummeln, auch wenn
 * jemand in die Entwicklerwerkzeuge schaut.
 *
 * Eine Frage nach der anderen, wie auf einer Lernplattform üblich: Wer alle
 * fünf untereinander sieht, scrollt zurück und vergleicht, statt zu überlegen.
 */
export default function ModuleQuiz() {
	const { t } = useTranslation()
	const history = useHistory()
	const { id: courseId, moduleId } = useParams()

	const { course, reload } = useCourse(courseId)
	const [quiz, setQuiz] = useState(null)
	const [fehler, setFehler] = useState(null)
	const [schritt, setSchritt] = useState(0)
	const [antworten, setAntworten] = useState({})
	const [ergebnis, setErgebnis] = useState(null)
	const [sendet, setSendet] = useState(false)
	const [konfetti, setKonfetti] = useState(0)

	const vorschau = course?.mode === 'PREVIEW'

	const holen = () => {
		setQuiz(null)
		setFehler(null)
		setErgebnis(null)
		setSchritt(0)
		setAntworten({})
		http.get(TApi.COURSE_QUIZ(moduleId), { disableToast: true })
			.then(({ data }) => setQuiz(data))
			.catch(err => setFehler(err?.response?.data?.message || t('The quiz could not be loaded.')))
	}

	useEffect(() => {
		holen()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [moduleId])

	const modulIndex = (course?.modules || []).findIndex(modul => String(modul.id) === String(moduleId))
	const modul = course?.modules?.[modulIndex]
	const fragen = quiz?.questions || []
	const frage = fragen[schritt]

	const waehlen = antwortId =>
		setAntworten(vorher => ({
			...vorher,
			[frage.id]: toggleAnswer(vorher[frage.id], antwortId, frage.multiple)
		}))

	const abgeben = () => {
		if (vorschau) return
		setSendet(true)
		http.post(TApi.COURSE_QUIZ(moduleId), { token: quiz.token, answers: antworten })
			.then(({ data }) => {
				setErgebnis(data)
				if (data.passed) setKonfetti(wert => wert + 1)
				reload()
			})
			.catch(() => {})
			.finally(() => setSendet(false))
	}

	const zurKursseite = () => history.push(TRoutes.COURSE(courseId))

	if (ergebnis?.justCompleted && ergebnis.completion) {
		return (
			<div className={styles.root}>
				<Confetti ausloeser={konfetti} />
				<Completion course={course} completion={ergebnis.completion} />
			</div>
		)
	}

	const naechstesModul = modulIndex >= 0 ? course?.modules?.[modulIndex + 1] : null
	const ersteLektionDanach = naechstesModul?.lessons?.[0]

	return (
		<div className={styles.root}>
			{vorschau && <PreviewBanner />}
			<Confetti ausloeser={konfetti} />

			<div className={styles.topBar}>
				<button type="button" className={styles.backLink} onClick={zurKursseite}>
					‹ {course?.title || t('Back to the course')}
				</button>
			</div>

			<div className={styles.quiz}>
				<span className={styles.quizHead}>
					{modulIndex >= 0 ? t('Module {{n}}', { n: modulIndex + 1 }) : t('Quiz')}
				</span>
				<h1 className={styles.quizTitle}>{modul ? `${t('Quiz')}: ${modul.title}` : t('Quiz')}</h1>

				{fehler && <p className={styles.error}>{fehler}</p>}
				{!fehler && !quiz && <p className={styles.loading}>{t('Loading…')}</p>}

				{quiz && !ergebnis && frage && (
					<>
						<div className={styles.steps} aria-hidden="true">
							{fragen.map((eintrag, index) => (
								<span
									key={eintrag.id}
									className={clsx((antworten[eintrag.id] || []).length > 0 && styles.stepDone)}
									style={index === schritt ? { outline: '2px solid #fff' } : undefined}
								/>
							))}
						</div>

						<p className={styles.quizHead}>
							{t('Question {{n}} of {{total}}', { n: schritt + 1, total: fragen.length })}
						</p>
						<h2 className={styles.question}>{frage.text}</h2>
						<p className={styles.multipleHint}>
							{frage.multiple ? t('Several answers can be correct.') : t('Choose one answer.')}
						</p>

						<div className={styles.answers} role={frage.multiple ? 'group' : 'radiogroup'}>
							{frage.answers.map((antwort, index) => {
								const gewaehlt = (antworten[frage.id] || []).includes(antwort.id)
								return (
									<button
										key={antwort.id}
										type="button"
										role={frage.multiple ? 'checkbox' : 'radio'}
										aria-checked={gewaehlt}
										className={clsx(styles.answer, gewaehlt && styles.answerSelected)}
										onClick={() => waehlen(antwort.id)}
									>
										<span
											className={clsx(styles.answerMark, frage.multiple && styles.answerMarkSquare)}
											aria-hidden="true"
										>
											{gewaehlt ? '✓' : String.fromCharCode(65 + index)}
										</span>
										{antwort.text}
									</button>
								)
							})}
						</div>

						<div className={styles.quizNav}>
							<button
								type="button"
								className={styles.ghost}
								disabled={schritt === 0}
								onClick={() => setSchritt(wert => wert - 1)}
							>
								‹ {t('Previous question')}
							</button>
							{schritt < fragen.length - 1 ? (
								<button
									type="button"
									className={styles.play}
									disabled={!(antworten[frage.id] || []).length}
									onClick={() => setSchritt(wert => wert + 1)}
								>
									{t('Next question')} ›
								</button>
							) : (
								<button
									type="button"
									className={styles.play}
									disabled={!allAnswered(fragen, antworten) || sendet || vorschau}
									onClick={abgeben}
									title={vorschau ? t('Not available in the preview.') : undefined}
								>
									{sendet ? t('Checking…') : t('Submit answers')}
								</button>
							)}
						</div>
					</>
				)}

				{ergebnis && (
					<div className={styles.result}>
						<div
							className={clsx(
								styles.resultScore,
								ergebnis.passed ? styles.resultPassed : styles.resultFailed
							)}
						>
							{ergebnis.correct} / {ergebnis.total}
						</div>
						<p className={styles.resultText}>
							{ergebnis.passed
								? t('Passed! The module is complete.')
								: t('Not passed yet — you need {{n}} correct answers. Watch the videos again and try once more.', {
										n: quiz?.quizPass ?? course?.quizPass
								  })}
						</p>

						<div className={styles.actions} style={{ justifyContent: 'center' }}>
							{!ergebnis.passed && (
								<button type="button" className={styles.play} onClick={holen}>
									{t('Try the quiz again')}
								</button>
							)}
							{ergebnis.passed && ersteLektionDanach && (
								<button
									type="button"
									className={styles.play}
									onClick={() => history.push(TRoutes.COURSE_LESSON(courseId, ersteLektionDanach.id))}
								>
									<span className={styles.triangle} aria-hidden="true" />
									{t('Next module')}
								</button>
							)}
							<button type="button" className={styles.ghost} onClick={zurKursseite}>
								{t('Back to the course')}
							</button>
						</div>

						<div className={styles.review}>
							{(ergebnis.results || []).map(eintrag => {
								const frageText = fragen.find(f => String(f.id) === String(eintrag.id))
								const richtig = (frageText?.answers || [])
									.filter(antwort => (eintrag.correctAnswerIds || []).map(String).includes(String(antwort.id)))
									.map(antwort => antwort.text)
								return (
									<div
										key={eintrag.id}
										className={clsx(styles.reviewItem, eintrag.correct && styles.reviewItemOk)}
									>
										<div className={styles.reviewQuestion}>
											{eintrag.correct ? '✓ ' : '✗ '}
											{frageText?.text}
										</div>
										{!eintrag.correct && richtig.length > 0 && (
											<div>
												{t('Correct')}: {richtig.join(' · ')}
											</div>
										)}
										{eintrag.explanation && (
											<div className={styles.reviewExplain}>{eintrag.explanation}</div>
										)}
									</div>
								)
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
